import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { AppBarTitleService, AppBarActionsService, AppBarAction, MessageService, FormHelper, AppPageService, SimpleDialogService, CommonActionKey } from 'common';
import { ApiKeyService } from '../api-key.service';
import { ApiKeyData } from '../api-key.model';
import { ClientData } from '../../../auth/client/client.data';
import { MatDialog } from '@angular/material/dialog';
import { UserPermissionService } from '../../../user/user-permission/user-permission.service';
import { readWrite } from '../../../user/user-permission/user-permission.data';
import { ApiKeyUpdateDialogComponent } from '../api-key-update-dialog/api-key-update-dialog.component';

@UntilDestroy()
@Component({
    selector: 'ifb-api-key-details',
    templateUrl: './api-key-details.component.html',
    styleUrls: ['./api-key-details.component.scss'],
    standalone: false
})
export class ApiKeyDetailsComponent implements OnInit {
  private deleteSub: Subscription;
  private saveSub: Subscription;

  form: UntypedFormGroup;
  data: ApiKeyDetailsComponentData;
  readWriteApiKey: boolean;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiKeyService: ApiKeyService,
    private readonly userPermissionService: UserPermissionService,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly messageService: MessageService,
    private readonly dialog: MatDialog,
    private readonly simpleDialogService: SimpleDialogService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.initData.bind(this));
    this.appBarActionsService.invoking.pipe(untilDestroyed(this)).subscribe(this.actionDispatch.bind(this));
    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(this.actionUpdate.bind(this));
    this.userPermissionService.granted([readWrite('admin-api-keys')])
      .subscribe(res => {
        this.readWriteApiKey = res;
        this.appBarActionsService.enable('delete', res);
      });
    this.initActions()
  }

  actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  cancel(): void {
    this.appPageService.back();
  }

  copy(inputElement: any): void {
    inputElement.select();
    document.execCommand('copy');
  }

  delete(): void {
    if (!this.data || !this.data.apiKey || !this.data.apiKey.id)
      this.appPageService.back();

    const message = `Delete api key?`;
    this.simpleDialogService.openSimpleConfirmDialog(message, "Confirm")
      .subscribe((result: CommonActionKey) => {
        if (result !== CommonActionKey.Confirm) {
          this.actionUpdate();
          return;
        }

        this.deleteSub = this.apiKeyService.delete(this.data.apiKey.id).subscribe(() => {
          this.appPageService.back();
        });
      });
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);
    if (this.saveSub)
      return;

    if (!this.form.valid)
      return;

    if (!this.form.dirty) {
      this.cancel();
      return;
    }

    if (this.data?.apiKey?.id) {
      ApiKeyUpdateDialogComponent.show(this.dialog, this.form, "Update").subscribe(result => {
        if (result) {
          this.saveSub = this.apiKeyService
            .update(this.data.apiKey.id, this.form.value)
            .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
          this.actionUpdate();
          this.appPageService.back();
        }
      });
    } else {
      const name = this.data.clients.find(_ => _.id === this.form.value.clientId).name;
      ApiKeyUpdateDialogComponent.show(this.dialog, this.form, "Create", name).subscribe(result => {
        if (result)
          this.saveSub = this.apiKeyService
            .create(this.form.value)
            .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
        this.actionUpdate();
      });
    }
  }

  private initActions(): void {
    setTimeout(() => {
      this.appBarActionsService.actions = [
        { id: 'delete', label: 'Delete', disabled: true, buttonType: 'button' },
        { id: 'cancel', label: 'Cancel', buttonType: 'button' },
        { id: 'save', label: 'Save', buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' }
      ];
      this.actionUpdate();
    }, 0);
  }

  private initForm(): void {
    this.form = new UntypedFormGroup({
      id: new UntypedFormControl(),
      clientId: new UntypedFormControl(undefined, Validators.required),
      description: new UntypedFormControl(),
      active: new UntypedFormControl(undefined, Validators.required),
      secret: new UntypedFormControl(undefined, Validators.maxLength(64)),
      createdBy: new UntypedFormControl(),
      createdOn: new UntypedFormControl()
    });
  }

  private initData(data: ApiKeyDetailsComponentData): void {
    this.data = data;
    if (data.apiKey)
      this.form.patchValue(data.apiKey);

    this.appBarTitleService.title = data.apiKey && data.apiKey.id
      ? `Api Key for ${data.apiKey.clientName}`
      : 'Create new api key';

    this.form.markAsPristine();
    this.actionUpdate();
  }

  private actionUpdate(): void {
    this.appBarActionsService.enable('delete', this.data && this.data.apiKey && this.data.apiKey.id && this.readWriteApiKey && !this.deleteSub ? true : false);
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
  }


  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveSuccessHandler(apiKey: ApiKeyData): void {
    this.saveSubClear();
    if (apiKey) {
      this.data.apiKey = apiKey;
      this.initData(this.data);
      this.actionUpdate();
      this.form.get('clientId').disable();
    } else {
      this.router.navigate(['admin', 'apikey', apiKey.id], { replaceUrl: true });
    }
  }
}

export interface ApiKeyDetailsComponentData {
  apiKey: ApiKeyData;
  clients: ClientData[];
}
